import type React from 'react';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type AvatarVariant = 'circle' | 'square' | 'rounded';
export type AvatarType = 'user' | 'assistant' | 'custom';
export type AvatarStatus = 'online' | 'offline' | 'away' | 'busy';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  fallback?: string;
  size?: AvatarSize;
  variant?: AvatarVariant;
  type?: AvatarType;
  status?: AvatarStatus;
  showStatus?: boolean;
  badge?: React.ReactNode;
}

const UserIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const AssistantIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

export function Avatar({ 
  src,
  alt,
  fallback,
  size = 'md',
  variant = 'circle',
  type = 'custom',
  status,
  showStatus = false,
  badge,
  className = '',
  ...props
}: AvatarProps) {
  // Size classes
  const sizeClasses = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-20 h-20'
  };

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8',
    '2xl': 'w-10 h-10'
  };

  const textSizes = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl'
  };

  // Variant classes
  const variantClasses = {
    circle: 'rounded-full',
    square: '',
    rounded: 'rounded-lg'
  };

  // Type-based styling
  const typeClasses = {
    user: 'bg-gradient-to-br from-primary-stop-1 to-primary-stop-2',
    assistant: 'bg-gradient-to-br from-success to-success-border',
    custom: 'bg-gradient-to-br from-bg-surface to-border-light'
  };

  // Status indicator classes
  const statusClasses = {
    online: 'bg-success-border',
    offline: 'bg-text-disabled',
    away: 'bg-warning',
    busy: 'bg-danger'
  };

  const baseClasses = [
    'relative',
    'flex',
    'items-center',
    'justify-center',
    'flex-shrink-0',
    'shadow-sm',
    'transition-all',
    'duration-200',
    'font-medium',
    'font-manrope'
  ];

  const classes = [
    ...baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    src ? 'overflow-hidden' : typeClasses[type],
    className
  ].filter(Boolean).join(' ');

  // Determine fallback content
  const getFallbackContent = () => {
    if (fallback) {
      return (
        <span className={`${textSizes[size]} text-white font-semibold`}>
          {fallback}
        </span>
      );
    }

    if (type === 'user') {
      return <UserIcon className={`${iconSizes[size]} text-white`} />;
    }

    if (type === 'assistant') {
      return <AssistantIcon className={`${iconSizes[size]} text-white`} />;
    }

    return (
      <svg className={`${iconSizes[size]} text-content-muted`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    );
  };

  return (
    <div className={classes} {...props}>
      {src ? (
        <img
          src={src}
          alt={alt ?? 'Avatar'}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const container = e.currentTarget.parentElement;
            if (container) {
              container.classList.add(...typeClasses[type].split(' '));
            }
          }}
        />
      ) : (
        getFallbackContent()
      )}
      
      {/* Status Indicator */}
      {showStatus && status && (
        <div className="absolute -bottom-0.5 -right-0.5">
          <div className={`w-3 h-3 rounded-full border-2 border-white ${statusClasses[status]}`} />
        </div>
      )}
      
      {/* Badge */}
      {badge && (
        <div className="absolute -top-1 -right-1">
          {badge}
        </div>
      )}
    </div>
  );
}

Avatar.displayName = 'Avatar';